import { NextPurchaseConflict, NextPurchaseUpdate } from '../../services/ServiceNextProject';

// Selection state for the conflict review: `${nextPurchaseId}:${field}` -> selected.
// Checked means "overwrite the Next value with the Accurator value on export";
// unchecked keeps the value currently in Next.
export type ConflictSelection = Record<string, boolean>;

export const selectionKey = (nextPurchaseId: number, field: string) => `${nextPurchaseId}:${field}`;

export const buildFullSelection = (conflicts: NextPurchaseConflict[]): ConflictSelection => {
  const selection: ConflictSelection = {};
  for (const purchase of conflicts) {
    for (const conflictField of purchase.fields) {
      selection[selectionKey(purchase.nextPurchaseId, conflictField.field)] = true;
    }
  }
  return selection;
};

export const toggleField = (selection: ConflictSelection, nextPurchaseId: number, field: string): ConflictSelection => ({
  ...selection,
  [selectionKey(nextPurchaseId, field)]: !selection[selectionKey(nextPurchaseId, field)],
});

export const setPurchaseSelected = (selection: ConflictSelection, purchase: NextPurchaseConflict, selected: boolean): ConflictSelection => {
  const next = { ...selection };
  for (const conflictField of purchase.fields) {
    next[selectionKey(purchase.nextPurchaseId, conflictField.field)] = selected;
  }
  return next;
};

export const setAllSelected = (conflicts: NextPurchaseConflict[], selected: boolean): ConflictSelection => {
  return selected ? buildFullSelection(conflicts) : {};
};

// Scoped variant for the list-header master checkbox: only touches the given
// purchases (e.g. the ones visible under an active field filter), leaving the
// selection of filtered-out purchases untouched.
export const setPurchasesSelected = (selection: ConflictSelection, purchases: NextPurchaseConflict[], selected: boolean): ConflictSelection => {
  let next = selection;
  for (const purchase of purchases) {
    next = setPurchaseSelected(next, purchase, selected);
  }
  return next;
};

export type PurchaseSelectionState = 'all' | 'some' | 'none';

export const purchaseSelectionState = (selection: ConflictSelection, purchase: NextPurchaseConflict): PurchaseSelectionState => {
  const selectedCount = purchase.fields.filter(f => selection[selectionKey(purchase.nextPurchaseId, f.field)]).length;
  if (selectedCount === 0) return 'none';
  return selectedCount === purchase.fields.length ? 'all' : 'some';
};

export const countSelectedFields = (selection: ConflictSelection, conflicts: NextPurchaseConflict[]): number =>
  conflicts.reduce((sum, purchase) =>
    sum + purchase.fields.filter(f => selection[selectionKey(purchase.nextPurchaseId, f.field)]).length, 0);

// The payload for POST /next-project/export: only purchases with at least one
// selected field are included.
export const selectionToUpdates = (selection: ConflictSelection, conflicts: NextPurchaseConflict[]): NextPurchaseUpdate[] =>
  conflicts
    .map(purchase => ({
      nextPurchaseId: purchase.nextPurchaseId,
      fields: purchase.fields.filter(f => selection[selectionKey(purchase.nextPurchaseId, f.field)]).map(f => f.field),
    }))
    .filter(update => update.fields.length > 0);

// Field-name chips with counts across all purchases, for the filter row.
export const conflictFieldCounts = (conflicts: NextPurchaseConflict[]): { field: string, count: number }[] => {
  const counts = new Map<string, number>();
  for (const purchase of conflicts) {
    for (const conflictField of purchase.fields) {
      counts.set(conflictField.field, (counts.get(conflictField.field) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([field, count]) => ({ field, count }))
    .sort((a, b) => b.count - a.count);
};
